/**
 * Home: what needs you (Abnahme 2026-09-20).
 *
 * Alan: "Home Screen sollte automatisch wichtige approvals oder action items
 * geben, oder ‚Hey, deine Delivery ist fertig, schau sie dir hier an'. Der
 * Status muss auf der Home Screen nicht gesehen werden, solange es nicht fertig
 * ist." So: approvals, unpriced briefs, finished deliveries. In-progress work
 * stays in Work. Renders nothing when nothing needs the customer.
 *
 * @module web/screens/app/work/ActionItems
 */
import { useCallback, useEffect, useState } from "react";
import { get } from "../../../api/client.ts";
import type { BriefListResponse } from "../../../api/types.ts";
import { fmtEurCompact } from "../../../money.ts";
import { BRIEFS_CHANGED } from "../confirmSheet.tsx";
import { actionItems, type ActionItem } from "./cards.ts";

export function ActionItems({ compact = false }: { compact?: boolean }) {
  const [items, setItems] = useState<ActionItem[] | null>(null);
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await get<BriefListResponse>("/api/briefs");
      setItems(actionItems(r.briefs ?? [], compact ? Number.POSITIVE_INFINITY : 6));
    } catch {
      // Decoration on Home: a failed read leaves the list as it was.
    }
  }, [compact]);

  useEffect(() => {
    void load();
    const onChange = () => { void load(); };
    window.addEventListener(BRIEFS_CHANGED, onChange);
    return () => window.removeEventListener(BRIEFS_CHANGED, onChange);
  }, [load]);

  if (!items || items.length === 0) return null;

  return (
    <section className={`needs${compact ? " needs-compact" : " fade-in"}`} id="needs-you" aria-label="Needs you">
      <div className="needs-heading">
        <h2 className="proj-day">Ready for your review</h2>
        {compact && items.length > 2 && <button type="button" className="needs-toggle" aria-expanded={expanded} aria-controls="needs-rows" onClick={() => setExpanded(value => !value)}>
          {expanded ? "Show fewer" : `Show all ${items.length}`}
        </button>}
      </div>
      <div id="needs-rows">
      {(compact && !expanded ? items.slice(0, 2) : items).map((it, i) => {
        const key = `${it.kind}-${it.href}-${i}`;
        if (it.kind === "retry") return <a className="needs-row" href={it.href} key={key} data-kind="retry"><span className="needs-ic" aria-hidden="true"><ClockIcon /></span><span className="needs-main"><span className="needs-t">{it.title}</span><span className="needs-s">Included in plan · needs another attempt</span></span><span className="needs-cta">Open Work</span></a>;
        if (it.kind === "approve") {
          return (
            <a className="needs-row" href={it.href} key={key} data-kind="approve">
              <span className="needs-ic approve" aria-hidden="true"><ReceiptIcon /></span>
              <span className="needs-main">
                <span className="needs-t">{it.title}</span>
                <span className="needs-s">{it.count > 1 ? `${it.count} cards · ` : ""}{fmtEurCompact(it.fareCents)} · ready to confirm</span>
              </span>
              <span className="needs-cta">Approve in Work</span>
            </a>
          );
        }
        if (it.kind === "price") {
          return (
            <a className="needs-row" href={it.href} key={key} data-kind="price">
              <span className="needs-ic" aria-hidden="true"><ClockIcon /></span>
              <span className="needs-main">
                <span className="needs-t">{it.title}</span>
                <span className="needs-s">{it.noScript ? "No spoken script found yet" : "Couldn't be priced automatically yet"}</span>
              </span>
              <span className="needs-cta">{it.noScript ? "Add the script" : "Price again"}</span>
            </a>
          );
        }
        return (
          <a className="needs-row" href={it.href} target="_blank" rel="noopener noreferrer" key={key} data-kind="delivered">
            <span className="needs-ic done" aria-hidden="true"><CheckIcon /></span>
            <span className="needs-main">
              <span className="needs-t">{it.title}</span>
              <span className="needs-s">Your delivery is ready{it.editor ? ` · ${it.editor}` : ""}</span>
            </span>
            <span className="needs-cta">View delivery</span>
          </a>
        );
      })}
      </div>
    </section>
  );
}

function ReceiptIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 3h12v18l-3-2-3 2-3-2-3 2z" /><path d="M9 8h6M9 12h6" />
    </svg>
  );
}
function ClockIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" /><path d="M12 7.5V12l3 2" />
    </svg>
  );
}
function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 6L9 17l-5-5" />
    </svg>
  );
}
